import AppSplashScreen from "components/AppSplashScreen";
import { useEffect } from "react";
import ReactGA from "react-ga4";
import AppRoutes from "routes";
import { useDispatch, useSelector } from "store";
import {
  selectAuthIsLoggedIn,
  selectAuthUser,
  selectIsOrganizationLoading,
  selectOrganization,
} from "store/selectors";
import { logout } from "store/slices/auth/authSlice";
import { getProfileAddresses } from "store/slices/profileAddresses/profileAddressesSlice";
import { setUserForTracking, trackEvent } from "tracking";
import { checkIsHomeServiceOrganization } from "utils/organization";

const TRACKING_ID = "G-NM0202GTDP"; // OUR_TRACKING_ID
ReactGA.initialize(TRACKING_ID);

export default function App() {
  const dispatch = useDispatch();

  const organization = useSelector(selectOrganization);
  const user = useSelector(selectAuthUser);
  const isLoggedIn = useSelector(selectAuthIsLoggedIn);
  const isOrganizationLoading = useSelector(selectIsOrganizationLoading);

  useEffect(() => {
    if (isLoggedIn) {
      ReactGA.set({ userId: user?.email });
    }
  }, [isLoggedIn, user?.email]);

  useEffect(() => {
    if (organization?.id && organization?.name) {
      trackEvent("Organization Visited", { id: organization.id, name: organization.name }, {});
    }
  }, [organization]);

  if (organization && organization?.id && organization?.name && user) {
    setUserForTracking(user, { id: organization.id, name: organization.name });
  }

  const isHomeService = checkIsHomeServiceOrganization(organization.industry!);

  useEffect(() => {
    if (isHomeService && isLoggedIn) {
      dispatch(getProfileAddresses({}));
    }
  }, [isHomeService, isLoggedIn, dispatch]);

  // Check for 10-minute timeout and log user out if exceeded
  useEffect(() => {
    if (!isLoggedIn) return;

    const checkTimeout = () => {
      const loginTimestamp = localStorage.getItem("login_timestamp");
      if (!loginTimestamp) return;

      const loginTime = parseInt(loginTimestamp, 10);
      const currentTime = Date.now();
      const tenMinutesInMs = 10 * 60 * 1000; // 10 minutes

      if (currentTime - loginTime >= tenMinutesInMs) {
        dispatch(logout());
      }
    };

    // Check immediately
    checkTimeout();

    // Check every minute
    const interval = setInterval(checkTimeout, 60 * 1000);

    return () => clearInterval(interval);
  }, [isLoggedIn, dispatch]);

  if (isOrganizationLoading) return <AppSplashScreen />;

  return <AppRoutes />;
}
